import { useMutation, useQuery } from 'react-query'

import { queryClient } from 'store/state'

import {
  connectIntegrationApi,
  disconnectIntegrationApi,
  getMyIntegrationsApi,
  getProvidersApi,
  getUserIntegrationsApi,
  startOAuthApi
} from './api'

const KEYS = {
  MINE: 'integrations/me',
  PROVIDERS: 'integrations/providers',
  USER: 'integrations/user'
}

export const useIntegrationProviders = () => {
  const { data, isLoading } = useQuery(
    KEYS.PROVIDERS,
    getProvidersApi
  )
  return { providers: data || [], isLoading }
}

export const useMyIntegrations = () => {
  const { data, isLoading, refetch } = useQuery(
    KEYS.MINE,
    getMyIntegrationsApi
  )

  const invalidate = () =>
    queryClient.invalidateQueries(KEYS.MINE)

  const connect = useMutation(connectIntegrationApi, {
    onSuccess: invalidate
  })

  const disconnect = useMutation(disconnectIntegrationApi, {
    onSuccess: invalidate
  })

  const findByProvider = providerKey =>
    (data || []).find(i => i.provider === providerKey) || null

  // Fetches the authorize URL from the backend, then full-page redirects.
  // We can't use a normal anchor because the request needs the bearer token.
  const startOAuth = async providerKey => {
    const { authorizeUrl } = await startOAuthApi(providerKey)
    if (authorizeUrl) {
      window.location.href = authorizeUrl
    }
  }

  return {
    integrations: data || [],
    isLoading,
    refetch,
    findByProvider,
    connect: connect.mutateAsync,
    isConnecting: connect.isLoading,
    connectError: connect.error,
    resetConnect: connect.reset,
    disconnect: disconnect.mutateAsync,
    isDisconnecting: disconnect.isLoading,
    startOAuth
  }
}

export const useUserIntegrations = userId => {
  const queryKey = [KEYS.USER, userId]
  const { data, isLoading, refetch } = useQuery(
    queryKey,
    () => getUserIntegrationsApi(userId),
    { enabled: Boolean(userId) }
  )

  const findByProvider = providerKey =>
    (data || []).find(i => i.provider === providerKey) || null

  return {
    integrations: data || [],
    isLoading,
    refetch,
    findByProvider
  }
}
